import { AccordionTitle } from '@/components/Shared/AccordionComponent'
import { DEFAULT_CONTAINER_ROW_STYLE } from '@/constants/scenarioBuilder.constant'
import {
  AccordionKeys,
  Liabilities,
  MethodTypes,
  OptimizationTypes,
  RiskModel,
} from '@/enums/portfolio-optimization.enum'
import { useAppSelector } from '@/hooks/use-app-selector'
import {
  CashFlowFileRecord,
  IPreAfterTaxFileRecord,
  IRateFileRecord,
} from '@/interfaces/portfolioOptimization.interface'
import {
  getContributionWithdrawal,
  getLiabilities,
  getObjectiveItems,
  getObjectiveTypes,
  getOptimizationTypes,
} from '@/services/portfolioOptimization.service'
import {
  setAccordionComplete,
  setOptimizationParams
} from '@/states/portfolioOptmization.slice'
import { showSnackbar } from '@/states/snackbar.slice'
import { SEMANTIC_COLOR_PALETTE } from '@/theme/theme-config'
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect, useReducer, useState } from 'react'
import { useDispatch } from 'react-redux'
import StatusIndicator from '../../StatusIndicator'
import BorrowingRateGrid from './BorrowingRatesGrid'
import LiabilityGrid from './LiabilityGrid'
import MultiSelectDropdown from './MultiSelectDropdown'
import {
  OptimizationParameterActionType,
  OptimizationParameterState,
  optimizationParameterInitialState,
  optimizationParameterReducer,
} from './OptimizationParameterAccordionState'
import PreAfterTaxRatesGrid from './PreAfterTaxRatesGrid'
import ReinvestmentRateGrid from './ReinvestmentRateGrid'
import { SelectBenchmark } from './SelectBenchmark'
import { SingleSelectDropdown } from './SingleSelectDropdown'

// Define the mapping for visible fields based on OptimizationType
const visibleFieldsMapping: Record<OptimizationTypes, string[]> = {
  [OptimizationTypes.Index]: [
    'methodType',
    'objectiveType',
    'objectiveItem',
    'riskModel',
    'benchmark',
    'contribution',
  ],
  [OptimizationTypes.Horizon]: [
    'objectiveType',
    'objectiveItem',
    'liabilities',
    'contribution',
  ],
  [OptimizationTypes.Dedication]: [
    'objectiveType',
    'objectiveItem',
    'liabilities',
  ],
  [OptimizationTypes.Scenario]: ['benchmark', 'contribution'],
}

const OptimizationParameterAccordion: React.FC = () => {
  const appDispatch = useDispatch()

  const isAccordionComplete = useAppSelector(
    (s) =>
      s.portfolioOptimization.accordions[AccordionKeys.OPTIMIZATION_PARAMETER],
  )

  const [state, dispatch] = useReducer(
    optimizationParameterReducer,
    optimizationParameterInitialState,
  )

  const optimizationParams = useAppSelector(
    (s) => s.portfolioOptimization.formData?.optimizationParams,
  )

  useEffect(() => {
    dispatch({
      type: OptimizationParameterActionType.SET_OPTIMIZATION_TYPE,
      payload: optimizationParams?.params?.optimizationType || '',
    })
    dispatch({
      type: OptimizationParameterActionType.SET_OBJECTIVE_TYPE,
      payload: optimizationParams?.params?.objectiveType || '',
    })
    dispatch({
      type: OptimizationParameterActionType.SET_OBJECTIVE_ITEM,
      payload: optimizationParams?.params?.objectiveItem || '',
    })
    if (
      optimizationParams?.params?.liabilityReinvestmentTaxes
        ?.specifyLiabilityToMatch
    ) {
      dispatch({
        type: OptimizationParameterActionType.SET_CASHFLOW_FILE_RECORDS,
        payload:
          optimizationParams?.params?.liabilityReinvestmentTaxes
            ?.liabilityToMatch || [],
      })
    }
    if (
      optimizationParams?.params?.liabilityReinvestmentTaxes
        ?.specifyReinvestmentRates
    ) {
      dispatch({
        type: OptimizationParameterActionType.SET_REINVESTMENT_RATE_FILE_RECORDS,
        payload:
          optimizationParams?.params?.liabilityReinvestmentTaxes
            ?.reinvestmentRates || [],
      })
    }
    if (
      optimizationParams?.params?.liabilityReinvestmentTaxes
        ?.specifyBorrowingRates
    ) {
      dispatch({
        type: OptimizationParameterActionType.SET_BORROWING_RATE_FILE_RECORDS,
        payload:
          optimizationParams?.params?.liabilityReinvestmentTaxes
            ?.borrowingRates || [],
      })
    }
    dispatch({
      type: OptimizationParameterActionType.SET_CONTRIBUTION,
      payload: optimizationParams?.params?.contributionWithdrawal || '',
    })
    dispatch({
      type: OptimizationParameterActionType.SET_CONTRIBUTION_AMOUNT,
      payload: optimizationParams?.params?.amount || 0.0,
    })

    // Initialize liabilities based on optimization parameters
    const initialLiabilities: string[] = []
    
    if (
      optimizationParams?.params?.liabilityReinvestmentTaxes
        ?.specifyLiabilityToMatch
    ) {
      initialLiabilities.push(Liabilities.SpecifyLiabilityToMatch)
    }
    
    if (
      optimizationParams?.params?.liabilityReinvestmentTaxes
        ?.specifyReinvestmentRates
    ) {
      initialLiabilities.push(Liabilities.SpecifyReinvestmentRates)
    }
    
    if (
      optimizationParams?.params?.liabilityReinvestmentTaxes
        ?.specifyBorrowingRates
    ) {
      initialLiabilities.push(Liabilities.SpecifyBorrowingRates)
    }

    dispatch({
      type: OptimizationParameterActionType.SET_LIABILITIES,
      payload: initialLiabilities,
    })
  }, [optimizationParams])

  const [loading, setLoading] = useState({
    contributionWithdrawal: false,
    liabilities: false,
    optimizationTypes: false,
    objectiveTypes: false,
    objectiveItems: false,
  })

  const [optimizationParameters, setOptimizationParameters] = useState<{
    contributionWithdrawal: string[]
    liabilities: string[]
    optimizationTypes: string[]
    objectiveTypes: string[]
    objectiveItems: string[]
  }>({
    contributionWithdrawal: [],
    liabilities: [],
    optimizationTypes: [],
    objectiveTypes: [],
    objectiveItems: [],
  })

  // Function to determine if a field should be visible based on the selected OptimizationType
  const isFieldVisible = (field: string): boolean => {
    const visibleFields = state.optimizationType
      ? visibleFieldsMapping[state.optimizationType as OptimizationTypes]
      : []
    return visibleFields.includes(field)
  }

  // Function to determine if all required fields are filled based on the selected OptimizationType
  // Ensure that state is of type OptimizationParameterState
  const isFormValid = (): boolean => {
    const visibleFields = state.optimizationType
      ? visibleFieldsMapping[state.optimizationType as OptimizationTypes]
      : undefined

    if (!visibleFields) return false

    // Check if all visible fields are filled
    const areFieldsFilled = visibleFields.every((field) => {
      if (field === 'liabilities' || field === 'contribution') {
        return true // Skip validation for these fields
      }
      return (
        state[field as keyof OptimizationParameterState] !== '' &&
        state[field as keyof OptimizationParameterState] !== null &&
        state[field as keyof OptimizationParameterState] !== undefined
      )
    })

    return areFieldsFilled
  }

  // Handle using the optimization parameters
  const onUseOptimizationParameter = () => {
    if (isFormValid()) {
      appDispatch(
        setAccordionComplete({
          accordionType: AccordionKeys.OPTIMIZATION_PARAMETER,
          isComplete: true,
        }),
      )
      dispatch({ type: OptimizationParameterActionType.TOGGLE_EXPANDED })
      const payload = optimizationPayload(state)
      appDispatch(setOptimizationParams(payload))
    } else {
      appDispatch(
        showSnackbar({
          message: 'Please fill all required fields',
          severity: 'error',
        }),
      )
    }
  }

  const optimizationPayload = (state: OptimizationParameterState) => {
    const isLiabilityToMatch = !!state.liabilities.find(
      (val) => val === Liabilities.SpecifyLiabilityToMatch,
    )
    const isReinvestmentRates = !!state.liabilities.find(
      (val) => val === Liabilities.SpecifyReinvestmentRates,
    )
    const isBorrowingRates = !!state.liabilities.find(
      (arr) => arr === Liabilities.SpecifyBorrowingRates,
    )

    const payload = {
      params: {
        optimizationType: state.optimizationType,
        objectiveType: state.objectiveType,
        objectiveItem: state.objectiveItem,
        liabilityReinvestmentTaxes: {
          specifyLiabilityToMatch: isLiabilityToMatch,
          specifyReinvestmentRates: isReinvestmentRates,
          specifyBorrowingRates: isBorrowingRates,
          useCashFlows: false, // Update when this property is enabled
          liabilityToMatch: state.cashFlowFileRecords,
          reinvestmentRates: state.rateFileRecords,
          borrowingRates: state.borrowingRateFileRecords,
          cashFlows: state.preAfterTaxFileRecords,
        },
        contributionWithdrawal: state.contribution,
        amount: state.amount,
      },
    }
    return payload
  }

  const getAllOptimizationParameters = async () => {
    try {
      setLoading((prev) => ({
        ...prev,
        optimizationTypes: true,
        objectiveTypes: true,
        objectiveItems: true,
        liabilities: true,
        contributionWithdrawal: true,
      }))

      const [
        optimizationTypesData,
        objectiveTypesData,
        objectiveItemsData,
        liabilitiesData,
        contributionWithdrawalData,
      ] = await Promise.all([
        getOptimizationTypes(),
        getObjectiveTypes(),
        getObjectiveItems(),
        getLiabilities(),
        getContributionWithdrawal(),
      ])

      setOptimizationParameters({
        contributionWithdrawal: contributionWithdrawalData,
        liabilities: liabilitiesData,
        optimizationTypes: optimizationTypesData,
        objectiveTypes: objectiveTypesData,
        objectiveItems: objectiveItemsData,
      })
    } catch (error: unknown) {
      setOptimizationParameters({
        contributionWithdrawal: [],
        liabilities: [],
        optimizationTypes: [],
        objectiveTypes: [],
        objectiveItems: [],
      })

      appDispatch(
        showSnackbar({
          message: `Error fetching some / all optimization parameters ${error instanceof Error ? error.message : String(error)}`,
          severity: 'error',
        }),
      )
    } finally {
      setLoading((prev) => ({
        ...prev,
        optimizationTypes: false,
        objectiveTypes: false,
        objectiveItems: false,
        liabilities: false,
        contributionWithdrawal: false,
      }))
    }
  }

  useEffect(() => {
    getAllOptimizationParameters()
  }, [])

  return (
    <Accordion
      expanded={state.isExpanded}
      onChange={() =>
        dispatch({ type: OptimizationParameterActionType.TOGGLE_EXPANDED })
      }
    >
      <AccordionTitle aria-controls='panel2-content' id='panel2-header'>
        <Typography variant='h3'>Optimization Parameters</Typography>
        <StatusIndicator isComplete={isAccordionComplete} />
      </AccordionTitle>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant='h5'>Optimization Parameters</Typography>
            <Box
              sx={{
                ...DEFAULT_CONTAINER_ROW_STYLE,
                gap: 1,
                marginTop: 1,
                flexWrap: 'wrap',
              }}
            >
              <SingleSelectDropdown
                data-cy='optimization-select'
                defaultValue={state.optimizationType}
                label='Optimization Type'
                options={optimizationParameters.optimizationTypes}
                loading={loading.optimizationTypes}
                onChange={(value) => {
                  dispatch({
                    type: OptimizationParameterActionType.SET_OPTIMIZATION_TYPE,
                    payload: value,
                  })
                  dispatch({
                    type: OptimizationParameterActionType.SET_COMPLETE,
                    payload: false,
                  })

                  dispatch({
                    type: OptimizationParameterActionType.SET_LIABILITIES,
                    payload:
                      value === OptimizationTypes.Dedication
                        ? [Liabilities.SpecifyLiabilityToMatch]
                        : [Liabilities.SpecifyLiabilityToMatch], // Keep checked by default for all types
                  })
                }}
              />
              {isFieldVisible('benchmark') && (
                <SelectBenchmark
                  onChange={(value) =>
                    dispatch({
                      type: OptimizationParameterActionType.SET_BENCHMARK,
                      payload: value,
                    })
                  }
                  defaultBenchmark={state.benchmark}
                />
              )}

              {isFieldVisible('methodType') && (
                <SingleSelectDropdown
                  defaultValue={state.methodType}
                  label='Method'
                  options={Object.values(MethodTypes)}
                  onChange={(value: string) =>
                    dispatch({
                      type: OptimizationParameterActionType.SET_METHOD_TYPE,
                      payload: value,
                    })
                  }
                />
              )}

              {isFieldVisible('objectiveType') && (
                <SingleSelectDropdown
                  defaultValue={state.objectiveType}
                  label='Objective Type'
                  options={optimizationParameters.objectiveTypes}
                  loading={loading.objectiveTypes}
                  onChange={(value) => {
                    dispatch({
                      type: OptimizationParameterActionType.SET_OBJECTIVE_TYPE,
                      payload: value,
                    })
                  }}
                />
              )}
              {isFieldVisible('objectiveItem') && (
                <SingleSelectDropdown
                  defaultValue={state.objectiveItem}
                  label='Objective Item'
                  options={optimizationParameters.objectiveItems}
                  loading={loading.objectiveItems}
                  onChange={(value) =>
                    dispatch({
                      type: OptimizationParameterActionType.SET_OBJECTIVE_ITEM,
                      payload: value,
                    })
                  }
                />
              )}
              {isFieldVisible('riskModel') && (
                <SingleSelectDropdown
                  defaultValue={state.riskModel}
                  label='Risk Model'
                  options={Object.values(RiskModel)}
                  onChange={(value) =>
                    dispatch({
                      type: OptimizationParameterActionType.SET_RISK_MODEL,
                      payload: value,
                    })
                  }
                />
              )}
              {isFieldVisible('liabilities') && (
                <MultiSelectDropdown
                  defaultValue={state.liabilities}
                  label='Liability, Reinvestment & Taxes'
                  disableOptions={[Liabilities.UsePreAfterTaxFlowsPreTaxCashFlows]}
                  options={optimizationParameters.liabilities}
                  loading={loading.liabilities}
                  onChange={(values) => {
                    dispatch({
                      type: OptimizationParameterActionType.SET_LIABILITIES,
                      payload: values,
                    })
                  }}
                />
              )}
              {isFieldVisible('contribution') && (
                <SingleSelectDropdown
                  defaultValue={state.contribution}
                  label='Contribution/Withdrawal'
                  options={optimizationParameters.contributionWithdrawal}
                  onChange={(value) =>
                    dispatch({
                      type: OptimizationParameterActionType.SET_CONTRIBUTION,
                      payload: value,
                    })
                  }
                />
              )}
              {isFieldVisible('contribution') && state.contribution && (
                <TextField
                  type='number'
                  size='small'
                  defaultValue={state.amount}
                  label='Amount'
                  onChange={(event: { target: { value: string } }) =>
                    dispatch({
                      type: OptimizationParameterActionType.SET_CONTRIBUTION_AMOUNT,
                      payload: parseFloat(event?.target?.value),
                    })
                  }
                />
              )}
            </Box>
          </Box>
          {/* Rendering Liabilities file upload box */}
          {isFieldVisible('liabilities') && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
              }}
            >
              {state.liabilities?.includes(
                Liabilities.SpecifyLiabilityToMatch,
              ) && (
                <LiabilityGrid
                  initialData={state.cashFlowFileRecords}
                  onChange={(records: CashFlowFileRecord[]) => {
                    dispatch({
                      type: OptimizationParameterActionType.SET_CASHFLOW_FILE_RECORDS,
                      payload: records,
                    })
                  }}
                />
              )}

              {state.liabilities?.includes(
                Liabilities.SpecifyReinvestmentRates,
              ) && (
                <ReinvestmentRateGrid
                  initialData={state.rateFileRecords}
                  onChange={(records: IRateFileRecord[]) => {
                    dispatch({
                      type: OptimizationParameterActionType.SET_REINVESTMENT_RATE_FILE_RECORDS,
                      payload: records,
                    })
                  }}
                />
              )}
              {state.liabilities?.includes(
                Liabilities.SpecifyBorrowingRates,
              ) && (
                <BorrowingRateGrid
                  initialData={state.borrowingRateFileRecords}
                  onChange={(records: IRateFileRecord[]) => {
                    dispatch({
                      type: OptimizationParameterActionType.SET_BORROWING_RATE_FILE_RECORDS,
                      payload: records,
                    })
                  }}
                />
              )}
              {state.liabilities?.includes(
                Liabilities.UsePreAfterTaxFlowsPreTaxCashFlows,
              ) && (
                <PreAfterTaxRatesGrid
                  onChange={(records: IPreAfterTaxFileRecord[]) => {
                    dispatch({
                      type: OptimizationParameterActionType.SET_PRE_AFTER_TAX_FILE_RECORDS,
                      payload: records,
                    })
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      </AccordionDetails>
      <AccordionActions
        sx={{
          padding: 2,
          borderTop: '1px solid',
          borderTopColor: SEMANTIC_COLOR_PALETTE.border.light,
        }}
      >
        <Button
          color='primary'
          size='small'
          onClick={() =>
            dispatch({ type: OptimizationParameterActionType.RESET })
          }
        >
          Reset
        </Button>
        <Button
          variant='contained'
          size='small'
          onClick={onUseOptimizationParameter}
          disabled={!isFormValid()} // Disable button based on form validity
        >
          Use Optimization Parameters
        </Button>
      </AccordionActions>
    </Accordion>
  )
}

export default OptimizationParameterAccordion
